/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import Imgix from 'js/components/Imgix';
import { getLanguageMatch, getUserLanguageCode, languageCodeToName } from 'js/lib/language';
import { getLabelForAutotranslation } from 'js/lib/languageLocalized';
import { isAuthenticatedUser } from 'js/lib/user';

import { Grid, LogoWrapper, Typography2, breakpoints, typography2Color } from '@coursera/cds-core';
import { GlobeIcon, QuizIcon } from '@coursera/cds-icons';
import type { ButtonName, SectionName } from '@coursera/event-pulse-types';
import { Button } from '@coursera/event-pulse/react';

import { useIntl } from 'bundles/page/lib/useIntl';
import Subsection from 'bundles/unified-description-page-common/components/shared/Subsection';
import { useEventingProductData } from 'bundles/unified-description-page-common/contexts/EventingContext';
import { useLanguageInfoDialog } from 'bundles/unified-description-page-common/contexts/LanguageInfoDialogContext';
import { usePageInfo } from 'bundles/unified-description-page-common/contexts/PageInfoContext';
import LanguageInfoDialogProvider, {
  type Languages,
} from 'bundles/unified-description-page-common/providers/LanguageInfoDialogProvider';
import { getAudienceLanguageCode } from 'bundles/unified-description-page-common/utils/dubbingUtil';
import getLanguagesMetadata from 'bundles/unified-description-page-common/utils/getLanguagesMetadata';

import linkedInLogo from 'images/unified-description-page/linkedin-logo.png';

import _t from 'i18n!nls/enterprise-learning-path-description-page';

const styles = {
  titleOverride: css`
    margin-bottom: calc(-1 * var(--cds-spacing-100));
  `,
  icon: css`
    width: auto;
    height: 24px;
  `,
  itemTitle: css`
    padding: var(--cds-spacing-100) 0;
  `,
  languageIcon: css`
    display: block;
  `,
  button: css`
    padding: 0;
    text-align: left;

    /* set vertical-align to unset to vertically center the button within the "Subtitles" line */
    vertical-align: unset;

    :hover {
      background: transparent;
    }

    ${typography2Color.supportText};
    font-weight: normal;
    text-decoration: underline;

    &:hover {
      ${typography2Color.supportText};
      text-decoration: none;
    }
  `,
  noSubtitle: css`
    padding: var(--cds-spacing-150) 0;
  `,
  itemSpacing: css`
    margin-top: var(--cds-spacing-200);
  `,
  aiDisclaimer: css`
    display: box;
    width: 100%;

    ${breakpoints.down('sm')} {
      margin-left: calc((var(--cds-spacing-400) + var(--cds-spacing-50)));
    }
  `,
};

const LanguagesDetailItem = ({ languages }: { languages: Languages }) => {
  const { openDialog } = useLanguageInfoDialog();
  const userLanguage = isAuthenticatedUser() ? getUserLanguageCode() : _t.getLocale();
  const eventingProductData = useEventingProductData();
  const { marketingSource } = usePageInfo();

  const trackingData = {
    button: { name: 'description_page_available_languages' as ButtonName },
    pageSection: {
      sectionName: 'about' as SectionName,
    },
    ...eventingProductData,
  };

  if (!languages.primaryLanguages?.length) {
    return null;
  }

  const { autoTranslations, humanTranslations, subtitles, originalLanguage, dubbedLanguages } =
    getLanguagesMetadata(languages);

  const userLanguageForAiDubbing = getAudienceLanguageCode(languages.dubbedLanguages);
  const enableAiDubbingOnXdp = !marketingSource && !!userLanguageForAiDubbing && dubbedLanguages.length > 0;

  // combine "available languages" and "video subtitles" into one section when XDP has AI dubbing languages
  const totalTranslations =
    dubbedLanguages.length > 0
      ? Array.from(new Set([...autoTranslations, ...humanTranslations, ...subtitles, ...dubbedLanguages])).length
      : autoTranslations.length + humanTranslations.length;

  let languageInfoLink: React.ReactNode = null;

  if (totalTranslations > 0) {
    const userLanguageAutoMatch =
      !!userLanguage && autoTranslations.find((language) => getLanguageMatch(userLanguage, language));
    const userLanguageHumanMatch =
      !!userLanguage && humanTranslations.find((language) => getLanguageMatch(userLanguage, language));
    let linkMessage: string;
    let matchedLanguageName = '';

    if (!enableAiDubbingOnXdp && (userLanguageAutoMatch || userLanguageHumanMatch)) {
      linkMessage = _t(
        '{languageCount, plural, =1 {# language available} other {# languages available}}, including {languageName}'
      );
      if (userLanguageHumanMatch) {
        matchedLanguageName = languageCodeToName(userLanguageHumanMatch);
      } else if (userLanguageAutoMatch) {
        matchedLanguageName = `${languageCodeToName(userLanguageAutoMatch)} (${getLabelForAutotranslation(
          userLanguageAutoMatch
        )})`;
      }
    } else {
      linkMessage = _t('{languageCount, plural, =1 {# language available} other {# languages available}}');
    }

    languageInfoLink = (
      <Button
        variant="ghost"
        size="small"
        onClick={openDialog}
        css={styles.button}
        trackingData={trackingData}
        noViewTracking
      >
        <FormattedMessage
          message={linkMessage}
          languageCount={totalTranslations + 1}
          languageName={matchedLanguageName}
        />
      </Button>
    );
  } else if (subtitles.length > 0) {
    languageInfoLink = (
      <Button
        variant="ghost"
        size="small"
        onClick={openDialog}
        css={styles.button}
        trackingData={trackingData}
        noViewTracking
      >
        {_t('Video subtitles available')}
      </Button>
    );
  }

  return (
    <Grid container spacing={8}>
      <Grid item>
        <GlobeIcon size="large" css={styles.languageIcon} />
      </Grid>
      <Grid item>
        <Typography2 variant="bodySecondary" component="div">
          {enableAiDubbingOnXdp ? (
            <FormattedMessage
              message={_t('Taught in {language} (AI Dubbing)')}
              language={languageCodeToName(userLanguageForAiDubbing)}
            />
          ) : (
            // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
            <FormattedMessage message={_t('Taught in {language}')} language={languageCodeToName(originalLanguage)} />
          )}
        </Typography2>
        {languageInfoLink && (
          <Typography2 variant="bodySecondary" color="supportText" component="div">
            {languageInfoLink}
          </Typography2>
        )}
      </Grid>
    </Grid>
  );
};

type Props = {
  totalAssignments: number;
  totalQuizzes: number;
  containsAiGrading?: boolean;
};

const getAssignmentAndQuizText = (
  intl: ReturnType<typeof useIntl>,
  totalAssignments: number,
  totalQuizzes: number,
  containsAiGrading?: boolean
): string => {
  const assignmentText = intl.formatMessage(
    _t('{totalAssignments, number} {totalAssignments, plural, =1 { assignment} other { assignments}}'),
    {
      totalAssignments,
    }
  );

  const quizText = intl.formatMessage(
    _t('{totalQuizzes, number} {totalQuizzes, plural, =1 { quiz} other { quizzes}}'),
    { totalQuizzes }
  );

  let assignmentAndQuizText: string;
  if (totalAssignments !== 0 && totalQuizzes !== 0) {
    assignmentAndQuizText = intl.formatMessage(_t('{quizText}, {assignmentText}'), {
      assignmentText,
      quizText,
    });
  } else if (totalAssignments !== 0) {
    assignmentAndQuizText = assignmentText;
  } else {
    assignmentAndQuizText = quizText;
  }

  return assignmentAndQuizText;
};

const IncludedSection = ({ totalAssignments, totalQuizzes, containsAiGrading }: Props) => {
  const intl = useIntl();
  // Dummy data for languages
  const dummyLanguages: Languages = {
    primaryLanguages: ['en'],
    translatedLanguages: [
      { code: 'es', isMachineTranslated: false },
      { code: 'fr', isMachineTranslated: true },
      { code: 'de', isMachineTranslated: false },
    ],
    subtitleLanguages: ['en', 'es', 'fr', 'de', 'zh'],
    dubbedLanguages: [{ languageCode: 'es' }, { languageCode: 'fr' }],
  };

  return (
    <Subsection
      title={
        <Typography2 component="h2" variant="subtitleMedium" css={styles.titleOverride}>
          {_t("What's included")}
        </Typography2>
      }
      spacing={16}
    >
      <LanguageInfoDialogProvider languages={dummyLanguages}>
        <Grid container spacing={8}>
          <Grid item container direction="column" spacing={8}>
            <Grid item>
              <Grid container spacing={8}>
                <Grid item>
                  <LogoWrapper paddingVariant="smallOutside">
                    <Imgix alt="" src={linkedInLogo} maxHeight={24} maxWidth={24} css={styles.icon} />
                  </LogoWrapper>
                </Grid>
                <Grid item>
                  <Typography2 variant="bodySecondary" component="div">
                    {_t('Shareable certificate')}
                  </Typography2>
                  <Typography2 component="p" variant="bodySecondary" color="supportText">
                    {_t('Add to your LinkedIn profile')}
                  </Typography2>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid item container>
            <LanguagesDetailItem languages={dummyLanguages} />
          </Grid>
          {(totalAssignments > 0 || totalQuizzes > 0) && (
            <Grid item container spacing={8}>
              <Grid item>
                <QuizIcon size="large" css={styles.icon} />
              </Grid>
              <Grid item>
                <Typography2 variant="bodySecondary" component="div">
                  {_t('Assessments')}
                </Typography2>
                <Typography2 component="p" variant="bodySecondary" color="supportText">
                  {getAssignmentAndQuizText(intl, totalAssignments, totalQuizzes, containsAiGrading)}
                </Typography2>
              </Grid>
            </Grid>
          )}
        </Grid>
      </LanguageInfoDialogProvider>
    </Subsection>
  );
};

export default IncludedSection;
